import { useState } from 'react'
import { Lock, MoreHorizontal, ShieldCheck, Plus } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Dropdown,
  IconButton,
  Table,
  Tooltip,
  type Column,
} from '@/components/ui'
import { QueryBoundary } from '@/components/domain'
import { useT } from '@/internationalization/i18n'
import { useCreateRole, useDeleteRole, useRoles, useUpdateRole } from './hooks'
import { PERMISSIONS, type RoleSummary } from './permissions'
import { usePermissions } from './use-permissions'
import { RoleEditor } from './components/RoleEditor'

/**
 * The role editor.
 *
 * WHAT A ROLE MEANS IS THIS WORKSPACE'S ANSWER, not the product's. Every
 * permission on every role — including the built-in four — is editable here,
 * which is the whole point: one customer's Admin administers the workspace and
 * never opens a recording, another's sees everything.
 *
 * TWO THINGS THE SERVER DECIDES AND THIS SCREEN ONLY RENDERS. `canEdit` per row,
 * because the Owner role in front of an admin holding every other permission is
 * not derivable in the browser — protection is "do you hold this role", not a
 * permission. And which permissions may be granted at all, because you cannot
 * grant one you do not hold yourself.
 */
export function RolesSettings() {
  const t = useT()
  const { data, isPending, error, refetch } = useRoles()
  const { has } = usePermissions()
  const canManage = has(PERMISSIONS.roleManage)

  const [editing, setEditing] = useState<RoleSummary | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const create = useCreateRole()
  const update = useUpdateRole()
  const remove = useDeleteRole()

  const isOpen = isCreating || editing !== null

  function close() {
    setIsCreating(false)
    setEditing(null)
  }

  const columns: Column<RoleSummary>[] = [
    {
      id: 'name',
      header: t('roles.col.role'),
      cell: (row) => (
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="truncate font-medium text-fg">{row.name}</span>
            {row.isProtected && (
              <Tooltip content={t('roles.protectedTip')}>
                <ShieldCheck className="h-3.5 w-3.5 text-brand" aria-label={t('roles.protected')} />
              </Tooltip>
            )}
          </span>
          {row.description && (
            <span className="block truncate text-sm text-fg-subtle">{row.description}</span>
          )}
        </span>
      ),
    },
    {
      id: 'access',
      header: t('roles.col.meetingAccess'),
      width: 'w-44',
      hideOnMobile: true,
      // The single most consequential thing about a role, so it is a column
      // rather than something you open a drawer to discover.
      cell: (row) =>
        row.permissions.includes(PERMISSIONS.meetingReadAll) ? (
          <Badge tone="warning">{t('roles.access.every')}</Badge>
        ) : row.permissions.includes(PERMISSIONS.meetingRead) ? (
          <Badge tone="info">{t('roles.access.own')}</Badge>
        ) : (
          <Badge tone="neutral">{t('roles.access.none')}</Badge>
        ),
    },
    {
      id: 'permissions',
      header: t('roles.col.permissions'),
      width: 'w-28',
      cell: (row) => <span className="text-fg-muted">{row.permissions.length}</span>,
    },
    {
      id: 'members',
      header: t('roles.col.members'),
      width: 'w-24',
      cell: (row) => <span className="text-fg-muted">{row.memberCount}</span>,
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      width: 'w-12',
      cell: (row) => (
        <Dropdown
          trigger={({ toggle }) => (
            <IconButton
              label={t('roles.manage', { name: row.name })}
              icon={row.canEdit ? <MoreHorizontal /> : <Lock />}
              size="sm"
              disabled={!row.canEdit && !row.canDelete}
              onClick={toggle}
            />
          )}
          items={[
            {
              id: 'edit',
              label: t('roles.editPermissions'),
              disabled: !row.canEdit,
              onSelect: () => setEditing(row),
            },
            {
              id: 'delete',
              label: t('roles.deleteRole'),
              destructive: true,
              separatorBefore: true,
              disabled: !row.canDelete,
              onSelect: () => remove.mutate(row.id),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <Card>
        <CardHeader
          title={t('roles.title')}
          description={t('roles.description')}
          action={
            canManage ? (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setIsCreating(true)}
              >
                {t('roles.newRole')}
              </Button>
            ) : undefined
          }
        />
        {!canManage && (
          <div className="px-4 pb-4 sm:px-5">
            <Alert tone="info" title={t('roles.readOnlyTitle')}>
              {t('roles.readOnlyBody')}
            </Alert>
          </div>
        )}
        <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
          <Table columns={columns} rows={data ?? []} rowKey={(row) => row.id} />
        </QueryBoundary>
      </Card>

      <RoleEditor
        role={isCreating ? null : editing}
        isNew={isCreating}
        isOpen={isOpen}
        isSaving={create.isPending || update.isPending}
        onClose={close}
        onSave={(body) => {
          if (isCreating) {
            create.mutate(body, { onSuccess: close })
            return
          }
          if (!editing) return
          update.mutate({ id: editing.id, patch: body }, { onSuccess: close })
        }}
      />
    </>
  )
}
